import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useState, type FormEvent } from 'react';
import { EASE, Reveal } from '../components/motion.tsx';
import { Avatar, Button, Callout, Empty, Field, PageHead, Panel, Skeleton } from '../components/ui.tsx';
import { ApiError, get, post, qs } from '../lib/api.ts';
import { time, todayIn } from '../lib/format.ts';

interface Log {
  id: string;
  takenAt: string;
  window: string | null;
  employee: { name: string; employeeNo: string | null };
}

export function Canteen() {
  const qc = useQueryClient();
  const [day, setDay] = useState(todayIn());
  const windows = useQuery({ queryKey: ['meal-windows'], queryFn: () => get<{ id: string; name: string; startsAt: string; endsAt: string }[]>('/api/canteen/windows') });
  const logs = useQuery({ queryKey: ['meal-logs', day], queryFn: () => get<Log[]>(`/api/canteen/logs${qs({ date: day })}`), refetchInterval: 20_000 });
  const [token, setToken] = useState('');
  const [last, setLast] = useState<{ ok: boolean; text: string } | null>(null);
  const scan = useMutation({
    mutationFn: () => post<{ employee: { name: string }; window: string }>('/api/canteen/scan', { qrToken: token.trim() }),
    onSuccess: (r) => {
      setLast({ ok: true, text: `${r.employee.name} · ${r.window}` });
      setToken('');
      void qc.invalidateQueries({ queryKey: ['meal-logs'] });
    },
    onError: (e) => setLast({ ok: false, text: e instanceof ApiError ? e.message : 'Scan failed' }),
  });
  return (
    <div>
      <PageHead
        eyebrow="Canteen"
        title={
          <>
            Lunch, <em>counted kindly.</em>
          </>
        }
        lede="Scan an employee QR at the counter. Hearth checks the serving window and the daily meal limit."
      />
      <Reveal className="grid-12">
        <Reveal.Item className="col-5">
          <Panel eyebrow="Counter" title="Scan">
            <form
              className="stack"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                if (token.trim()) scan.mutate();
              }}
            >
              <Field label="QR code" hint="A handheld scanner types into this field and presses Enter.">
                {(p) => <input {...p} className="input input--mono" autoFocus value={token} onChange={(e) => setToken(e.target.value)} placeholder="Scan now…" />}
              </Field>
              <Button variant="primary" type="submit" loading={scan.isPending}>
                Record meal
              </Button>
              <AnimatePresence mode="wait">
                {last ? (
                  <motion.div key={last.text} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3, ease: EASE }}>
                    <Callout tone={last.ok ? 'info' : 'bad'}>{last.text}</Callout>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </form>
          </Panel>
          <Panel eyebrow="Serving windows" title="Today" className="" bodyClassName="">
            <ul className="list-plain">
              {windows.data?.map((w) => (
                <li key={w.id} className="mini-row">
                  {w.name}
                  <span className="mini-row__aside">
                    {w.startsAt.slice(0, 5)}–{w.endsAt.slice(0, 5)}
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
        </Reveal.Item>
        <Reveal.Item className="col-7">
          <Panel eyebrow={`${logs.data?.length ?? 0} meals`} title="Served" action={<input type="date" className="input" style={{ width: 160, height: 32 }} value={day} onChange={(e) => setDay(e.target.value)} aria-label="Date" />}>
            {logs.isLoading ? (
              <Skeleton h={200} />
            ) : !logs.data?.length ? (
              <Empty title="No meals yet." />
            ) : (
              <ul className="list-plain">
                {logs.data.map((l) => (
                  <li key={l.id} className="mini-row">
                    <Avatar name={l.employee.name} size={28} />
                    {l.employee.name}
                    <span className="faint">{l.window}</span>
                    <span className="mini-row__aside">{time(l.takenAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </Reveal.Item>
      </Reveal>
    </div>
  );
}
